/**
 * collection-load-more.js
 *
 * Replaces automatic infinite scroll on standard collection pages with a
 * user-initiated "Load More" button, and updates the browser URL to the
 * paginated URL via the History API (history.pushState) as each page loads.
 *
 * Rationale (SEO brief, "Replace Infinite Scroll with Load More Button"):
 *   - Products stay associated with their correct paginated URLs.
 *   - Crawlable next-page links remain in the HTML (.pagination__next +
 *     .seo-pagination-nav are still rendered server-side).
 *   - Navigation becomes explicit rather than automatic.
 *
 * Performance: pages are fetched via the lightweight `view=ajax` template
 * (templates/collection.ajax.liquid — renders the SAME collection-template
 * section, so product-card markup is identical, at ~40% of the full-page
 * weight). The URL pushed to history is the canonical one WITHOUT the view
 * parameter, so users/search engines only ever see /collections/x?page=N.
 *
 * Resilience: Metafizzy's Button module permanently hides the button and
 * disables loading on ANY failed request (non-200 or network error). We take
 * over the 'error' event to restore the button as a "Try Again" so a flaky
 * connection can't strand the user with no way to continue browsing.
 *
 * Exposes window.initCollectionLoadMore(), which is idempotent per
 * #AjaxinateLoop element and is called from:
 *   - the collection template inline scripts on DOMContentLoaded, and
 *   - theme.js after an AJAX filter/sort re-renders the product grid.
 *
 * Requires Metafizzy InfiniteScroll (infinite-scroll.pkgd.js) to be loaded.
 */
(function () {
  'use strict';

  var LOOP_SELECTOR = '#AjaxinateLoop';
  var BTN_SELECTOR = '.collection-load-more__btn';
  var FOOTER_SELECTOR = '.collection-load-more';

  // Max products cap (from collection.metafields.custom.max_products, exposed as
  // window.MAX_PRODUCTS by the template). "null"/""/NaN => no limit.
  function getMax() {
    var n = parseInt(window.MAX_PRODUCTS, 10);
    return n > 0 ? n : null;
  }

  function gridCount(loop) {
    // Product cells only. Promo tiles injected into the grid (banner-promo's
    // #my-sale-banner, legacy .banner-item) also carry .Grid__Cell but never
    // data-soldout, so they must not count toward "viewed X of Y" or the
    // MAX_PRODUCTS cap.
    return loop.querySelectorAll('.Grid__Cell[data-soldout]').length;
  }

  function updateCounter(loop) {
    var countEl = document.querySelector(FOOTER_SELECTOR + ' [data-load-more-count]');
    if (countEl) countEl.textContent = gridCount(loop);
  }

  function getButton() {
    return document.querySelector(BTN_SELECTOR);
  }

  function hideButton() {
    var btn = getButton();
    if (btn) btn.style.display = 'none';
  }

  // Loading indicator lives on the button itself (no separate spinner element).
  function setLoading(on) {
    var btn = getButton();
    if (btn) btn.classList.toggle('is-loading', on);
  }

  function setLabel(text) {
    var label = document.querySelector(BTN_SELECTOR + ' .collection-load-more__btn-label');
    if (label) label.textContent = text;
  }

  // Mirror the theme's original per-load callback behaviour so appended
  // products behave like the initial ones.
  function afterAppend() {
    if (window.innerWidth <= 768) {
      document.querySelectorAll('.banner-item').forEach(function (el) { el.remove(); });
    } else if (typeof window.tinySliderInt === 'function') {
      window.tinySliderInt();
    }
  }

  function revealSoldOut() {
    if (typeof window.revealSoldOut === 'function') window.revealSoldOut();
  }

  function init() {
    if (typeof InfiniteScroll === 'undefined') return;

    var loop = document.querySelector(LOOP_SELECTOR);
    if (!loop) return;
    // Idempotent per element. A fresh grid injected by AJAX filtering is a new
    // node without this flag, so it re-initialises correctly.
    if (loop.dataset.loadMoreInit === '1') return;
    loop.dataset.loadMoreInit = '1';

    var max = getMax();

    updateCounter(loop);

    // No next page (single page, or already at the cap) -> nothing to load.
    // Everything is already in the DOM and 'last' will never fire, so the
    // sold-out cells must be revealed here or they stay hidden forever.
    var nextLink = document.querySelector('.pagination__next');
    if (!nextLink) {
      hideButton();
      revealSoldOut();
      return;
    }
    if (max && gridCount(loop) >= max) {
      nextLink.remove();
      hideButton();
      revealSoldOut();
      return;
    }

    // Derive paginated URLs from the server-rendered next link (it already
    // carries any active filter/sort params). Fetch the view=ajax variant;
    // push the canonical URL without the view param.
    var baseUrl = null;
    try {
      baseUrl = new URL(nextLink.getAttribute('href'), window.location.origin);
    } catch (e) { /* malformed href — fall back to fetching full pages */ }

    var startPage = 2;
    if (baseUrl) {
      var parsed = parseInt(baseUrl.searchParams.get('page'), 10);
      if (parsed > 1) startPage = parsed;
    }

    function urlForPage(page, forFetch) {
      var u = new URL(baseUrl.href);
      u.searchParams.set('page', page);
      if (forFetch) {
        u.searchParams.set('view', 'ajax');
      } else {
        u.searchParams.delete('view');
      }
      return u.pathname + '?' + u.searchParams.toString();
    }

    var options = {
      append: LOOP_SELECTOR + ' .Grid__Cell',
      button: BTN_SELECTOR,
      scrollThreshold: false, // user-initiated only — this removes infinite scroll
      // With a function path the library can't infer the next-link selector,
      // so tell checkLastPage explicitly: a response without .pagination__next
      // is the last page (fires 'last', which hides the button).
      checkLastPage: '.pagination__next',
      hideNav: '.pagination',
      history: false // we push the paginated URL ourselves, on the click
    };
    if (baseUrl) {
      // loadCount only increments on success, so a failed request re-fetches
      // the same page on retry. (Do NOT stash state here — the library calls
      // getPath at times of its own choosing, e.g. while resolving absolute
      // paths, so only the return value at request time is trustworthy.)
      options.path = function () {
        return urlForPage(startPage + this.loadCount, true);
      };
    } else {
      options.path = '.pagination__next';
    }

    var infScroll = new InfiniteScroll(loop, options);

    // Show the loading state on the button while a page is being fetched.
    infScroll.on('request', function () { setLoading(true); });

    // Recover from failed requests. The library sets canLoad=false and its
    // Button module hides the button (its handlers run before ours), which
    // would otherwise permanently strand the user mid-collection.
    infScroll.on('error', function () {
      setLoading(false);
      infScroll.canLoad = true;
      var btn = getButton();
      if (btn) {
        btn.style.display = '';
        btn.removeAttribute('disabled');
      }
      setLabel('Try Again');
    });

    // Update the browser URL to the just-loaded paginated URL. `path` is the
    // URL that was actually fetched — canonicalise it by dropping the
    // view=ajax fetch param.
    infScroll.on('load', function (response, path) {
      setLoading(false);
      setLabel('Load More');
      if (!path) return;
      var cleanPath = path;
      try {
        var u = new URL(path, window.location.origin);
        u.searchParams.delete('view');
        cleanPath = u.pathname + '?' + u.searchParams.toString();
      } catch (e) { /* keep path as-is */ }
      try {
        history.pushState(null, document.title, cleanPath);
      } catch (e) { /* pushState can throw on some browsers — non-fatal */ }
    });

    infScroll.on('append', function () {
      afterAppend();
      updateCounter(loop);

      if (max && gridCount(loop) >= max) {
        infScroll.destroy();
        var capNext = document.querySelector('.pagination__next');
        if (capNext) capNext.remove();
        hideButton();
        revealSoldOut();
      }
    });

    // InfiniteScroll's button auto-hides on 'last'; also reveal sold-out items.
    infScroll.on('last', function () {
      revealSoldOut();
    });
  }

  window.initCollectionLoadMore = init;
})();
